import type { UTCDate } from '@date-fns/utc';
import { clamp, startOfISOWeek } from 'date-fns';

import type {
  FilterConfig,
  FilterDefinition,
  FilterHandler,
} from '../engine/types';
import { type DateFilter, FilterId, FilterType } from '../types/filters';

export const snapshotDateDefinition: FilterDefinition<DateFilter> = {
  id: FilterId.SNAPSHOT_DATE,
  name: 'Date',
  value: null,
  type: FilterType.DATE,
};

const getRawDateOrDefault = (
  rawValue: UTCDate | null,
  systemDefault: UTCDate
): UTCDate => {
  return rawValue && rawValue !== null ? rawValue : systemDefault;
};

export const snapshotDateHandler: FilterHandler<DateFilter> = {
  isActive(_currentFilterState: DateFilter) {
    return true;
  },
  isDefault(
    currentFilterState: DateFilter,
    definition: FilterDefinition<DateFilter>
  ) {
    return currentFilterState.value === definition.value;
  },
  getEffectiveFilter(
    currentFilterState: DateFilter,
    definition: FilterDefinition<DateFilter>,
    config: FilterConfig
  ) {
    const { availableDateRange, viewGranularity, viewDateFilterId } = config;

    if (!availableDateRange || !availableDateRange[viewGranularity]) {
      return {
        ...currentFilterState,
        value: null,
      };
    }

    const { min: systemMin, max: systemMax } =
      availableDateRange[viewGranularity];

    if (!systemMin || !systemMax) {
      console.warn(
        'snapshotDateHandler.getEffectiveFilter: Invalid bounds for granularity',
        viewGranularity,
        { min: systemMin, max: systemMax }
      );
      return {
        ...currentFilterState,
        value: null,
      };
    }

    const snapshotDateSource = getRawDateOrDefault(
      currentFilterState.value,
      systemMax
    );
    let effectiveDate = clamp(snapshotDateSource, {
      start: systemMin,
      end: systemMax,
    }) as UTCDate;

    if (effectiveDate && viewGranularity === 'week') {
      effectiveDate = startOfISOWeek(effectiveDate) as UTCDate;
    }

    return {
      ...currentFilterState,
      value: effectiveDate,
      min: effectiveDate,
      max: effectiveDate,
      isUserSubmitted: !this.isDefault(currentFilterState, definition),
      isVisible:
        viewDateFilterId === definition.id &&
        !this.isDefault(currentFilterState, definition),
    };
  },
};
